import React, { useState, useEffect } from 'react';
import { BarChart3, Filter, Download, Star, FileText, Calendar, ChevronDown, Printer, FileSpreadsheet } from 'lucide-react';
import ReportsList from '../components/reports/ReportsList';
import ReportViewer from '../components/reports/ReportViewer';
import ReportFilters from '../components/reports/ReportFilters';
import { supabase } from '../lib/supabase';
import type { ReportType, ReportData, ReportFilter } from '../types/reports';

const Reports: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<ReportData | null>(null);
  const [currentType, setCurrentType] = useState<ReportType>('sales');
  const [filters, setFilters] = useState<ReportFilter>({
    period: 'month'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [reports, setReports] = useState<ReportData[]>([]);

  useEffect(() => {
    loadReports();
  }, [currentType, filters]);

  const loadReports = async () => {
    try {
      setIsLoading(true);
      
      // Load data based on report type
      switch (currentType) {
        case 'sales':
          await loadSalesReports();
          break;
        case 'financial':
          await loadFinancialReports();
          break;
        case 'inventory':
          await loadInventoryReports();
          break;
        case 'customers':
          await loadCustomerReports();
          break;
        case 'fiscal':
          await loadFiscalReports();
          break;
      }
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSalesReports = async () => {
    const { data: sales } = await supabase
      .from('sales')
      .select('*')
      .order('created_at', { ascending: false });

    if (sales) {
      const totalSales = sales.reduce((sum, sale) => sum + sale.total, 0);
      const averageTicket = totalSales / (sales.length || 1);

      const salesByPeriod = {
        labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
        datasets: [{
          label: 'Vendas',
          data: [30000, 35000, 28000, 32000, 40000, 38000],
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 2
        }]
      };

      setReports([{
        id: '1',
        type: 'sales',
        title: 'Vendas por Período',
        description: 'Relatório detalhado de vendas com análise temporal',
        filters,
        data: {
          totalSales,
          averageTicket,
          totalOrders: sales.length,
          salesByPeriod
        },
        createdAt: new Date(),
        createdBy: 'Sistema',
        favorite: true
      }]);
    }
  };

  const loadFinancialReports = async () => {
    // Implement financial reports loading
  };

  const loadInventoryReports = async () => {
    // Implement inventory reports loading
  };

  const loadCustomerReports = async () => {
    // Implement customer reports loading
  };

  const loadFiscalReports = async () => {
    // Implement fiscal reports loading
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    if (!selectedReport) return;

    try {
      // Prepare report data
      const reportData = {
        title: selectedReport.title,
        filters: selectedReport.filters,
        data: selectedReport.data,
        exportedAt: new Date().toISOString(),
      };

      if (format === 'pdf') {
        // Export as PDF
        window.print();
      } else {
        // Export as Excel
        const blob = new Blob([JSON.stringify(reportData)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedReport.title.toLowerCase().replace(/\s+/g, '-')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  const handleFilterChange = (newFilters: ReportFilter) => {
    setFilters(newFilters);
    setShowFilters(false);
  };

  const handleToggleFavorite = async (reportId: string) => {
    try {
      const report = reports.find(r => r.id === reportId);
      if (!report) return;

      const { error } = await supabase
        .from('reports')
        .update({ favorite: !report.favorite })
        .eq('id', reportId);

      if (error) throw error;

      setReports(reports.map(r => 
        r.id === reportId ? { ...r, favorite: !r.favorite } : r
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Relatórios e Gráficos
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Análise detalhada do seu negócio
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 border border-gray-200 shadow-sm flex items-center space-x-2"
              >
                <Calendar className="h-5 w-5" />
                <span>Período</span>
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => handleExport('excel')}
              className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 border border-gray-200 shadow-sm flex items-center"
            >
              <FileSpreadsheet className="h-5 w-5 mr-2" />
              Excel
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 border border-gray-200 shadow-sm flex items-center"
            >
              <Printer className="h-5 w-5 mr-2" />
              PDF
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mb-6">
            <ReportFilters
              filters={filters}
              onChange={handleFilterChange}
              onClose={() => setShowFilters(false)}
            />
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Report Types */}
          <div className="col-span-2">
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
              <h2 className="text-lg font-semibold mb-4">Categorias</h2>
              <nav className="space-y-2">
                <button
                  onClick={() => setCurrentType('sales')}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    currentType === 'sales'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Vendas
                </button>
                <button
                  onClick={() => setCurrentType('financial')}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    currentType === 'financial'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Financeiro
                </button>
                <button
                  onClick={() => setCurrentType('inventory')}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    currentType === 'inventory'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Estoque
                </button>
                <button
                  onClick={() => setCurrentType('customers')}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    currentType === 'customers'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Clientes
                </button>
                <button
                  onClick={() => setCurrentType('fiscal')}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    currentType === 'fiscal'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Fiscal
                </button>
              </nav>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 mb-3">
                  Favoritos
                </h3>
                <div className="space-y-2">
                  {reports
                    .filter(report => report.favorite)
                    .map(report => (
                      <button
                        key={report.id}
                        onClick={() => setSelectedReport(report)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center"
                      >
                        <Star className="h-4 w-4 text-yellow-400 mr-2" />
                        {report.title}
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Reports List and Viewer */}
          <div className="col-span-10">
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
              </div>
            ) : selectedReport ? (
              <ReportViewer
                report={selectedReport}
                onBack={() => setSelectedReport(null)}
                onExport={handleExport}
                onToggleFavorite={handleToggleFavorite}
              />
            ) : (
              <ReportsList
                type={currentType}
                reports={reports}
                onSelect={setSelectedReport}
                onToggleFavorite={handleToggleFavorite}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;